import type { ComponentPropsWithoutRef } from 'react';
import { Locale, Segment } from '@fixter/i18n';
import { z } from 'zod';
import { NotFoundError } from 'restify-errors';
import type { ContentComponent, ContentComponentFetchServerSideData } from '$cms/ContentComponent';
import { CmsShortTextSchema, CmsImageSchema, CmsRichTextSchema } from '$cms/types';
import type { ContentfulContentComponent } from '$cms/contentful.types';
import type { SchemaType } from '$util/schema';
import { fetchSymptomPageCollectionData } from '$server/modules/cms/fetchSymptomPageCollectionData';
import { makeContentComponentTranslatedEntities } from '@/server/cms/util/translatedEntities';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import { Card } from '@/components/atoms/Card';
import { Img } from '@/components/atoms/Img';
import { Button, DesignType } from '@/components/atoms/Button';
import { Col, Row } from '@/components/atoms/Grid';
import type { ServerLogger } from '@/server/logger';

import styles from './index.module.scss';

export interface SymptomsListProps extends ComponentPropsWithoutRef<'div'> {}

export const SymptomsListSchema = {
  translatedEntities: z.object({
    title: CmsShortTextSchema,
    learnMore: CmsShortTextSchema,
  }),
  serverSideData: z
    .object({
      symptoms: z
        .object({
          urlPath: z.string(),
          configuration: z.object({
            name: CmsShortTextSchema,
            category: CmsShortTextSchema,
            icon: CmsImageSchema,
            title: CmsShortTextSchema,
            shortDescription: CmsRichTextSchema,
            publishedAt: CmsShortTextSchema.optional(),
          }),
        })
        .array(),
    })
    .nullable(),
};

export type SymptomsListTranslatedEntities = SchemaType<(typeof SymptomsListSchema)['translatedEntities']>;
export type SymptomsListServerSideData = SchemaType<(typeof SymptomsListSchema)['serverSideData']>;

/**
 * A content component that displays all symptoms as cards
 * https://www.notion.so/fixter/SymptomsList-e16c562597e043eea5ddcfc08ebbecd0?pvs=4
 */
export const SymptomsList: ContentComponent<
  SymptomsListTranslatedEntities,
  null,
  SymptomsListProps,
  SymptomsListServerSideData
> = function SymptomsList({ translatedEntities, serverSideData, ...props }): JSX.Element | null {
  const { title, learnMore } = translatedEntities;
  if (!serverSideData) return null;
  const { symptoms } = serverSideData;
  return (
    <div className={styles.SymptomsList} {...props}>
      <h2 className={styles.heading}>{title.value}</h2>
      <Row>
        {symptoms.map((symptom) => (
          <Col key={symptom.configuration.name.value} extraSmall={4} small={6} className={styles.Col}>
            <Card cornerRadius={2} className={styles.SymptomCard}>
              <a className={styles.symptomHeading} href={symptom.urlPath}>
                <div className={styles.titles}>
                  <div className={styles.category}>{symptom.configuration.category.value}</div>
                  <h3 className={styles.name}>{symptom.configuration.name.value}</h3>
                </div>
                <div className={styles.iconContainer}>
                  <Img
                    provider="cloudinary"
                    src={symptom.configuration.icon.url}
                    alt={symptom.configuration.name.value}
                    className={styles.icon}
                    fill
                  />
                </div>
              </a>
              <h4 className={styles.title}> {symptom.configuration.title.value}</h4>
              <div className={styles.description}>
                {symptom.configuration.shortDescription &&
                  renderRichTextContent(symptom.configuration.shortDescription.content)}
              </div>
              <div>
                <Button
                  designType={DesignType.SECONDARY_DARK}
                  className={styles.learnMore}
                  href={symptom.urlPath}
                >
                  {learnMore.value}
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};
SymptomsList.displayName = 'SymptomsList';
SymptomsList.schema = SymptomsListSchema;

interface PageCollectionItemType {
  configuration: ContentfulContentComponent;
  urlPath: string;
}

export const fetchServerSideData: ContentComponentFetchServerSideData<SymptomsListServerSideData> = async (
  { segment },
  preview,
  container,
  req
) => {
  const logger = container.get<ServerLogger>('dynamicRouteLogger');
  const log = logger.traceRequest(req).child({ label: `${SymptomsList.displayName}.fetchServerSideData` });
  const locale = segment === Segment.Fixter_FR ? Locale.FR_FR : Locale.EN_GB;

  try {
    log.info('Fetch symptoms collection data');
    const { symptomPageCollectionCmsData } = await fetchSymptomPageCollectionData({ locale, preview });

    if (!symptomPageCollectionCmsData.items.length) {
      throw new NotFoundError('Not found symptoms');
    }

    const symptoms = symptomPageCollectionCmsData.items.map((item: PageCollectionItemType) => ({
      urlPath: item.urlPath,
      configuration: makeContentComponentTranslatedEntities(
        item.configuration.translatedEntitiesCollection.items
      ),
    }));

    return SymptomsListSchema.serverSideData.parse({ symptoms });
  } catch (error) {
    log.error({ error }, 'Failed to get symptoms data');
    return null;
  }
};
